// Modal "Contratar plano" / "Pagar fatura" — casca AerosDialog com o
// indicador de etapas (Plano · Pagamento) e o checkout transparente
// (components/SubscriptionCheckout). Carrega `GET /subscriptions/checkout` a
// cada abertura e decide o modo:
// - assinatura ACTIVE/PAST_DUE com fatura aberta ⇒ paga a fatura (rota
//   `/subscriptions/invoices/:id/pay`, sem escolha de período);
// - senão ⇒ contratação (`POST /subscriptions`).
// `Invoice` (linha clicada no Financeiro) só desempata qual fatura pagar.
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckIcon from "@material-ui/icons/Check";

import AerosDialog from "../AerosDialog";
import SubscriptionCheckout from "../SubscriptionCheckout";
import { formatBRL, modoDoCheckout } from "../SubscriptionCheckout/checkoutRules";
import useSubscription from "../../hooks/useSubscription";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    steps: { display: "flex", alignItems: "center", gap: 8 },
    step: { display: "flex", alignItems: "center", gap: 6 },
    dot: {
      width: 22,
      height: 22,
      borderRadius: "50%",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    dotDone: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      borderColor: "transparent",
      color: "#07080D",
    },
    dotCurrent: {
      backgroundColor: alpha("#A044FF", 0.18),
      borderColor: "rgba(160,68,255,0.6)",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    label: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary },
    labelOn: { color: theme.palette.text.primary },
    center: {
      minHeight: 240,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: 24,
      textAlign: "center",
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    retry: { minHeight: 44 },
    // Abaixo de 820px o indicador de etapas sai (mockup) para o título caber.
    stepsWrap: { "@media (max-width:820px)": { display: "none" } },
    sep: {
      width: 24,
      height: 1,
      backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.12)",
      margin: "0 4px",
    },
  };
});

const STEPS = ["subscription.checkout.stepPlan", "subscription.checkout.stepPayment"];

const SubscriptionModal = ({ open, onClose, Invoice }) => {
  const classes = useStyles();
  const { getCheckout } = useSubscription();
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [session, setSession] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setCheckout(await getCheckout());
    } catch (err) {
      setFailed(true);
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [getCheckout]);

  // Cada abertura busca de novo (fatura pode ter sido paga em outra aba) e
  // remonta o checkout zerado.
  useEffect(() => {
    if (!open) return;
    setCheckout(null);
    setSession((n) => n + 1);
    load();
  }, [open, load]);

  // Recarga completa: o Route decide `/financeiro-aberto` por
  // `user.company.dueDate` do AuthContext, que só muda ao recarregar o
  // usuário — `history.push("/")` voltaria para cá.
  const handlePaid = useCallback(() => {
    onClose();
    window.location.assign("/");
  }, [onClose]);

  const fatura = (() => {
    if (!checkout || modoDoCheckout(checkout) !== "invoice") return null;
    const clicada =
      Invoice?.id && Invoice.status === "open" && Invoice.subscriptionId
        ? Invoice
        : null;
    return clicada || checkout.openInvoice;
  })();

  const subtitle = (() => {
    if (!checkout) return undefined;
    if (fatura) {
      return i18n.t("subscription.checkout.subtitleInvoice", {
        date: moment(fatura.dueDate).format("DD/MM"),
        value: formatBRL(fatura.value),
      });
    }
    return i18n.t("subscription.checkout.subtitleNew", {
      plan: checkout.plan?.name || "",
      date: moment(checkout.cycleStartsToday).format("DD/MM"),
    });
  })();

  // Plano já está decidido ao abrir (o da empresa); a etapa atual é sempre o
  // pagamento — só fica "concluída" quando o modal fecha confirmado.
  const stepIndicator = (
    <div className={`${classes.steps} ${classes.stepsWrap}`}>
      {STEPS.map((key, index) => {
        const done = index === 0;
        const current = index === STEPS.length - 1;
        return (
          <div key={key} className={classes.step}>
            <div
              className={`${classes.dot} ${
                done ? classes.dotDone : current ? classes.dotCurrent : ""
              }`}
            >
              {done ? <CheckIcon /> : index + 1}
            </div>
            <span className={`${classes.label} ${classes.labelOn}`}>
              {i18n.t(key)}
            </span>
            {index < STEPS.length - 1 && <div className={classes.sep} />}
          </div>
        );
      })}
    </div>
  );

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={980}
      title={i18n.t(
        fatura ? "subscription.checkout.titleInvoice" : "subscription.checkout.titleNew"
      )}
      subtitle={subtitle}
      headerRight={stepIndicator}
      flushBody
      footer={null}
    >
      {checkout && !loading ? (
        <SubscriptionCheckout
          key={session}
          checkout={checkout}
          fatura={fatura}
          onPaid={handlePaid}
        />
      ) : (
        <div className={classes.center}>
          {failed ? (
            <>
              {i18n.t("subscription.checkout.loadError")}
              <Button variant="outlined" className={classes.retry} onClick={load}>
                {i18n.t("subscription.checkout.retry")}
              </Button>
            </>
          ) : (
            <CircularProgress size={28} />
          )}
        </div>
      )}
    </AerosDialog>
  );
};

export default SubscriptionModal;
